import React, { useState } from 'react';
import { equipementsAPI, mouvementsAPI, statsAPI } from '../utils/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import toast from 'react-hot-toast';

export default function Rapport() {
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState('inventaire');

  const genererRapport = async () => {
    setLoading(true);
    try {
      if (type === 'inventaire') await genererInventaire();
      else if (type === 'mouvements') await genererMouvements();
      else await genererComplet();
    } catch (e) {
      toast.error('Erreur lors de la génération');
      console.error(e);
    } finally { setLoading(false); }
  };

  const styles = `
    body { font-family: Arial, sans-serif; font-size: 12px; color: #1a1f2e; margin: 20px; }
    h1 { color: #e8192c; font-size: 20px; border-bottom: 2px solid #e8192c; padding-bottom: 8px; }
    h2 { color: #1a1f2e; font-size: 15px; margin-top: 20px; }
    .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
    .meta { color: #6b7280; font-size: 11px; }
    table { width: 100%; border-collapse: collapse; margin-top: 10px; }
    th { background: #1a1f2e; color: white; padding: 8px 10px; text-align: left; font-size: 11px; }
    td { padding: 7px 10px; border-bottom: 1px solid #f3f4f6; font-size: 11px; }
    tr:nth-child(even) { background: #f9fafb; }
    .badge { padding: 2px 8px; border-radius: 20px; font-size: 10px; font-weight: 600; }
    .neuf { background: #ecfdf5; color: #10b981; }
    .bon { background: #eff6ff; color: #3b82f6; }
    .usagé { background: #fffbeb; color: #f59e0b; }
    .défectueux { background: #fef2f2; color: #ef4444; }
    .entrée { background: #ecfdf5; color: #10b981; }
    .sortie { background: #fef2f2; color: #ef4444; }
    .ajustement { background: #fffbeb; color: #f59e0b; }
    .stat-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 12px; margin: 16px 0; }
    .stat-box { border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; text-align: center; }
    .stat-num { font-size: 24px; font-weight: 700; color: #e8192c; }
    .stat-lbl { font-size: 10px; color: #6b7280; margin-top: 4px; }
    .alerte { background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; padding: 8px 12px; margin: 8px 0; color: #ef4444; font-size: 11px; }
    @media print { button { display: none; } }
  `;

  const genererInventaire = async () => {
    const [eqRes, statsRes] = await Promise.all([equipementsAPI.getAll({}), statsAPI.get()]);
    const equipements = eqRes.data;
    const stats = statsRes.data;
    const alertes = equipements.filter(e => e.quantite <= (e.quantite_min ?? e.quantiteMin ?? 2));

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Rapport Inventaire — NetStock</title><style>${styles}</style></head><body>
      <div class="header">
        <div><h1>⬡ NetStock — Rapport Inventaire</h1><div class="meta">T&P Partners SARL · Douala, Cameroun</div></div>
        <div class="meta" style="text-align:right">Généré le ${format(new Date(), 'dd MMMM yyyy à HH:mm', { locale: fr })}</div>
      </div>
      <div class="stat-grid">
        <div class="stat-box"><div class="stat-num">${stats.totalEquipements}</div><div class="stat-lbl">Équipements</div></div>
        <div class="stat-box"><div class="stat-num">${stats.totalCategories}</div><div class="stat-lbl">Catégories</div></div>
        <div class="stat-box"><div class="stat-num" style="color:${alertes.length > 0 ? '#ef4444' : '#10b981'}">${alertes.length}</div><div class="stat-lbl">Alertes stock</div></div>
        <div class="stat-box"><div class="stat-num" style="color:#10b981">${(stats.valeurTotale || 0).toLocaleString('fr-FR')}</div><div class="stat-lbl">Valeur FCFA</div></div>
      </div>
      ${alertes.length > 0 ? `<div class="alerte">⚠️ ${alertes.length} équipement(s) sous le seuil minimal : ${alertes.map(e => e.nom).join(', ')}</div>` : ''}
      <h2>Liste complète des équipements</h2>
      <table>
        <thead><tr><th>Référence</th><th>Nom</th><th>Catégorie</th><th>Marque</th><th>État</th><th>Stock</th><th>Min</th><th>Emplacement</th><th>Prix unit. FCFA</th></tr></thead>
        <tbody>
          ${equipements.map(e => `
            <tr>
              <td style="font-family:monospace;color:#6b7280">${e.reference}</td>
              <td><strong>${e.nom}</strong></td>
              <td>${e.categories?.nom || e.categorie?.nom || '—'}</td>
              <td>${e.marque || '—'} ${e.modele || ''}</td>
              <td><span class="badge ${e.etat}">${e.etat}</span></td>
              <td style="font-weight:700;color:${e.quantite <= (e.quantite_min ?? 2) ? '#ef4444' : '#10b981'}">${e.quantite}</td>
              <td style="color:#6b7280">${e.quantite_min ?? e.quantiteMin ?? 2}</td>
              <td>${e.emplacement || '—'}</td>
              <td>${(e.prix_unitaire || 0).toLocaleString('fr-FR')}</td>
            </tr>`).join('')}
        </tbody>
      </table>
      <div style="margin-top:30px;color:#9ca3af;font-size:10px;text-align:center;border-top:1px solid #f3f4f6;padding-top:10px">
        NetStock · T&P Partners SARL · Rapport généré automatiquement
      </div>
    </body></html>`;

    ouvrirEtImprimer(html, 'rapport-inventaire');
  };

  const genererMouvements = async () => {
    const mvRes = await mouvementsAPI.getAll({ limit: 500 });
    const mouvements = mvRes.data;

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Rapport Mouvements — NetStock</title><style>${styles}</style></head><body>
      <div class="header">
        <div><h1>⬡ NetStock — Historique Mouvements</h1><div class="meta">T&P Partners SARL · Douala, Cameroun</div></div>
        <div class="meta" style="text-align:right">Généré le ${format(new Date(), 'dd MMMM yyyy à HH:mm', { locale: fr })}</div>
      </div>
      <div class="stat-grid">
        <div class="stat-box"><div class="stat-num">${mouvements.length}</div><div class="stat-lbl">Total opérations</div></div>
        <div class="stat-box"><div class="stat-num" style="color:#10b981">${mouvements.filter(m => m.type === 'entrée').length}</div><div class="stat-lbl">Entrées</div></div>
        <div class="stat-box"><div class="stat-num" style="color:#ef4444">${mouvements.filter(m => m.type === 'sortie').length}</div><div class="stat-lbl">Sorties</div></div>
        <div class="stat-box"><div class="stat-num" style="color:#f59e0b">${mouvements.filter(m => m.type === 'ajustement').length}</div><div class="stat-lbl">Ajustements</div></div>
      </div>
      <h2>Détail des mouvements</h2>
      <table>
        <thead><tr><th>Date</th><th>Équipement</th><th>Type</th><th>Qté</th><th>Avant</th><th>Après</th><th>Projet</th><th>Opérateur</th><th>Motif</th></tr></thead>
        <tbody>
          ${mouvements.map(m => `
            <tr>
              <td style="white-space:nowrap">${m.created_at ? format(new Date(m.created_at), 'dd/MM/yyyy HH:mm', { locale: fr }) : '—'}</td>
              <td><strong>${m.equipement?.nom || m.equipements?.nom || '—'}</strong><br><span style="color:#9ca3af;font-size:10px">${m.equipement?.reference || ''}</span></td>
              <td><span class="badge ${m.type}">${m.type}</span></td>
              <td style="font-weight:700;color:${m.type === 'entrée' ? '#10b981' : '#ef4444'}">${m.type === 'entrée' ? '+' : '-'}${m.quantite}</td>
              <td>${m.quantite_avant ?? m.quantiteAvant ?? '—'}</td>
              <td>${m.quantite_apres ?? m.quantiteApres ?? '—'}</td>
              <td>${m.projet || '—'}</td>
              <td>${m.operateur || '—'}</td>
              <td>${m.motif || '—'}</td>
            </tr>`).join('')}
        </tbody>
      </table>
      <div style="margin-top:30px;color:#9ca3af;font-size:10px;text-align:center;border-top:1px solid #f3f4f6;padding-top:10px">
        NetStock · T&P Partners SARL · Rapport généré automatiquement
      </div>
    </body></html>`;

    ouvrirEtImprimer(html, 'rapport-mouvements');
  };

  const genererComplet = async () => {
    await genererInventaire();
    setTimeout(() => genererMouvements(), 1000);
  };

  const ouvrirEtImprimer = (html, nom) => {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (win) {
      win.onload = () => { win.print(); };
      toast.success('Rapport prêt — utilisez Imprimer → Enregistrer en PDF');
    } else {
      // Fallback — téléchargement direct
      const a = document.createElement('a');
      a.href = url;
      a.download = `${nom}-${format(new Date(), 'yyyy-MM-dd')}.html`;
      a.click();
      toast.success('Rapport téléchargé');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Rapports</div>
        <div className="page-subtitle">Générer et télécharger des rapports PDF</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>

        {/* Rapport inventaire */}
        <div className="stat-card" style={{ padding: 24 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📦</div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Rapport Inventaire</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.5 }}>
            Liste complète de tous les équipements avec leur état, stock, emplacement et valeur totale.
          </div>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
            onClick={() => { setType('inventaire'); setTimeout(genererInventaire, 100); }}
            disabled={loading}>
            {loading && type === 'inventaire' ? '⏳ Génération...' : '⬇ Télécharger PDF'}
          </button>
        </div>

        {/* Rapport mouvements */}
        <div className="stat-card" style={{ padding: 24 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔄</div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Rapport Mouvements</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.5 }}>
            Historique complet de toutes les entrées, sorties et ajustements de stock avec opérateurs et projets.
          </div>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
            onClick={() => { setType('mouvements'); setTimeout(genererMouvements, 100); }}
            disabled={loading}>
            {loading && type === 'mouvements' ? '⏳ Génération...' : '⬇ Télécharger PDF'}
          </button>
        </div>

        {/* Rapport complet */}
        <div className="stat-card" style={{ padding: 24, borderTop: '3px solid var(--primary)' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Rapport Complet</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.5 }}>
            Inventaire + mouvements en deux fichiers séparés. Idéal pour un bilan mensuel complet.
          </div>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
            onClick={() => { setType('complet'); setTimeout(genererComplet, 100); }}
            disabled={loading}>
            {loading && type === 'complet' ? '⏳ Génération...' : '⬇ Tout télécharger'}
          </button>
        </div>

      </div>

      {/* Instructions */}
      <div style={{
        marginTop: 24, background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', padding: 20, boxShadow: 'var(--shadow-sm)',
      }}>
        <div style={{ fontWeight: 600, marginBottom: 10 }}>📌 Comment enregistrer en PDF</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            '1. Cliquez sur "Télécharger PDF" — une nouvelle fenêtre s\'ouvre',
            '2. La boîte d\'impression s\'affiche automatiquement',
            '3. Dans "Destination", choisissez "Enregistrer en PDF"',
            '4. Cliquez "Enregistrer" et choisissez votre emplacement',
          ].map((step, i) => (
            <div key={i} style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span>{step}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
